import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuthStore } from '../../store/authStore';
import {
  House, EnvelopeSimple, Image, Gear, Users, Translate,
  Buildings, Briefcase, Globe, ChatCircle, FolderOpen,
  CaretDown, SignOut, Buildings as CompanyIcon,
} from '@phosphor-icons/react';

const COMPANIES = [
  { key: 'india', label: 'ICON ECC India', color: '#E8913A', domain: 'iconecc.co.in' },
  { key: 'uae', label: 'ICON Overseas UAE', color: '#C9A84C', domain: 'uae.iconecc.co.in' },
  { key: 'infra', label: 'ICON Infra Technical Svcs', color: '#FF6B2B', domain: 'iconeccinfra.ae' },
  { key: 'tbcc', label: 'TBCC Technical Services LLC', color: '#14B8A6', domain: 'tbcc.iconeccinfra.ae' },
];

const CONTENT_TYPES = [
  { key: 'hero', label: 'Hero Slides', icon: Image },
  { key: 'services', label: 'Services', icon: Briefcase },
  { key: 'projects', label: 'Projects', icon: Buildings },
  { key: 'team', label: 'Team Members', icon: Users },
  { key: 'clients', label: 'Clients', icon: Globe },
  { key: 'certifications', label: 'Certifications', icon: FolderOpen },
  { key: 'testimonials', label: 'Testimonials', icon: ChatCircle },
  { key: 'blog', label: 'Blog / News', icon: FolderOpen },
  { key: 'careers', label: 'Careers', icon: Briefcase },
  { key: 'offices', label: 'Offices', icon: Buildings },
  { key: 'seo', label: 'SEO Settings', icon: Globe },
];

const TOP_NAV = [
  { to: '/dashboard', label: 'Dashboard', icon: House },
  { to: '/leads', label: 'All Leads', icon: EnvelopeSimple },
  { to: '/media', label: 'Media', icon: Image },
  { to: '/companies', label: 'Companies', icon: CompanyIcon },
];

const BOTTOM_NAV = [
  { to: '/settings', label: 'Settings', icon: Gear },
  { to: '/settings/users', label: 'Users & RBAC', icon: Users },
  { to: '/settings/i18n', label: 'Translations', icon: Translate },
  { to: '/settings/translations', label: 'Website Text', icon: Translate },
];

export default function Sidebar({ open, setOpen }) {
  const { user, logout, canAccess } = useAuthStore();
  const [expandedCompany, setExpandedCompany] = useState('infra');

  const navLinkClass = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all duration-150 ${isActive
      ? 'bg-[var(--color-accent)]/15 text-[var(--color-accent)] font-medium'
      : 'text-[var(--color-muted)] hover:text-[var(--color-text)] hover:bg-black/[0.04]'
    }`;

  return (
    <aside
      className="fixed top-0 left-0 h-full z-30 flex flex-col
                 bg-[var(--color-surface)] border-r border-[var(--color-border)]
                 transition-all duration-300 overflow-hidden"
      style={{ width: open ? 'var(--admin-sidebar-w)' : '60px' }}
    >
      <div className="flex items-center gap-3 px-4 h-[60px] border-b border-[var(--color-border)] flex-shrink-0">
        <div className="w-8 h-8 rounded-lg bg-[var(--color-accent)] flex items-center justify-center flex-shrink-0">
          <span className="text-white font-bold text-sm">I</span>
        </div>
        {open && (
          <div className="overflow-hidden">
            <div className="text-[var(--color-text)] font-semibold text-sm whitespace-nowrap">ICON Admin</div>
            <div className="text-[var(--color-muted)] text-[10px] whitespace-nowrap">Management Portal</div>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-hidden py-4 space-y-1 px-2">
        {TOP_NAV.map(({ to, label, icon: Icon }) => (
          <NavLink key={to} to={to} className={navLinkClass} title={!open ? label : undefined}>
            <Icon size={18} className="flex-shrink-0" />
            {open && <span className="whitespace-nowrap">{label}</span>}
          </NavLink>
        ))}

        {open && (
          <div className="pt-3 pb-1 px-3">
            <span className="text-[10px] font-bold uppercase tracking-widest text-[var(--color-muted)]/60">
              Content
            </span>
          </div>
        )}

        {COMPANIES.filter(c => !canAccess || canAccess(c.key)).map(co => (
          <div key={co.key}>
            <button
              onClick={() => setExpandedCompany(p => p === co.key ? null : co.key)}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm
                         text-[var(--color-muted)] hover:text-[var(--color-text)] hover:bg-black/[0.04]
                         transition-all duration-150"
              title={!open ? co.label : undefined}
            >
              <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: co.color }} />
              {open && (
                <>
                  <span className="flex-1 text-left whitespace-nowrap truncate text-xs font-medium">{co.label}</span>
                  <CaretDown size={12}
                    className={`transition-transform ${expandedCompany === co.key ? 'rotate-180' : ''}`} />
                </>
              )}
            </button>

            <AnimatePresence>
              {open && expandedCompany === co.key && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }}
                  className="overflow-hidden pl-5 mt-0.5 space-y-0.5"
                >
                  {CONTENT_TYPES.map(({ key, label, icon: Icon }) => (
                    <NavLink key={key} to={`/content/${co.key}/${key}`} className={navLinkClass}>
                      <Icon size={14} className="flex-shrink-0" />
                      <span className="whitespace-nowrap text-xs">{label}</span>
                    </NavLink>
                  ))}
                  <a
                    href={`https://${co.domain}`}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-3 px-3 py-2 rounded-lg text-xs text-[var(--color-muted)] hover:text-[var(--color-text)] hover:bg-black/[0.04] transition-all"
                  >
                    <Globe size={14} className="flex-shrink-0" />
                    <span className="whitespace-nowrap">Visit Live Site</span>
                  </a>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>

      <div className="border-t border-[var(--color-border)] px-2 py-3 space-y-1 flex-shrink-0">
        {BOTTOM_NAV.map(({ to, label, icon: Icon }) => (
          <NavLink key={to} to={to} className={navLinkClass} title={!open ? label : undefined}>
            <Icon size={18} className="flex-shrink-0" />
            {open && <span className="whitespace-nowrap text-xs">{label}</span>}
          </NavLink>
        ))}
        <button onClick={logout}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm
                     text-[var(--color-muted)] hover:text-red-500 hover:bg-red-500/10 transition-all"
          title={!open ? 'Sign Out' : undefined}>
          <SignOut size={18} className="flex-shrink-0" />
          {open && <span className="whitespace-nowrap text-xs">Sign Out</span>}
        </button>
      </div>
    </aside>
  );
}